import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Plus,
  X,
  Wand2,
  Loader,
  AlertCircle,
  ArrowRight,
  Search,
} from 'lucide-react';
import { StakeholderSelector, type SelectedStakeholder } from '../StakeholderSelector';

// ---------- shared types ----------

interface Filing {
  id_submission: string;
  filers?: Array<{ name: string }>;
  date_received?: string;
  date_submission?: string;
  submissiontype?: { short: string };
  filing_type?: string;
}

interface NprmFile {
  name: string;
  path: string;
  created_at: string;
}
type PublicNoticeFile = NprmFile;

interface FilingTypeInfo {
  label: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}

interface PeriodColors {
  bg: string;
  border: string;
  text: string;
  badge: string;
  button: string;
}

export interface GenerateSummaryFormProps {
  // Topics
  topics: string[];
  topicsInputMode: 'form' | 'paragraph';
  onTopicsChange: (topics: string[]) => void;
  onInputModeChange: (mode: 'form' | 'paragraph') => void;
  topicsFirstFieldRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>;

  // Scope — primary (in-window)
  selectionMode: 'all' | 'specific';
  selectedFilingIds: Set<string>;
  filings: Filing[];
  commentsCloseOn: string | null;
  onSelectionModeChange: (mode: 'all' | 'specific') => void;
  onFilingToggle: (submissionId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;

  // Scope — out-of-window (new opt-in)
  outOfWindowFilings: Filing[];
  selectedOutOfWindowIds: Set<string>;
  onOutOfWindowToggle: (submissionId: string) => void;
  onOutOfWindowSelectAll: () => void;
  onOutOfWindowDeselectAll: () => void;

  // Scope — mis-classified filings (NPRMs/Notices/etc. that may
  // actually be comments). Opt-in; backend extends the storage-folder
  // search to cover their filing types when selected.
  misclassifiedFilings: Filing[];
  selectedMisclassifiedIds: Set<string>;
  onMisclassifiedToggle: (submissionId: string) => void;
  onMisclassifiedSelectAll: () => void;
  onMisclassifiedDeselectAll: () => void;

  // Detail level
  detailLevel: 'concise' | 'standard' | 'detailed';
  onDetailLevelChange: (level: 'concise' | 'standard' | 'detailed') => void;

  // Stakeholders
  docketNumber: string;
  selectedStakeholders: SelectedStakeholder[];
  onStakeholdersChange: (stakeholders: SelectedStakeholder[]) => void;

  // Document picker (NPRM or PN — exactly one shows)
  showNprmPicker: boolean;
  nprmFiles: NprmFile[];
  selectedNprmFile: string | null;
  onNprmFileChange: (file: string | null) => void;
  isLoadingNprmFiles: boolean;
  isFetchingNprmFiles: boolean;
  fetchingNprmError: string | null;
  onFetchNprmFiles: () => void;
  nprmCount: number | null;

  showPnPicker: boolean;
  publicNoticeFiles: PublicNoticeFile[];
  selectedPublicNoticeFile: string | null;
  onPublicNoticeFileChange: (file: string | null) => void;
  isLoadingPnFiles: boolean;
  isFetchingPnFiles: boolean;
  fetchingPnError: string | null;
  onFetchPnFiles: () => void;
  publicNoticeCount: number | null;

  // Helpers
  getFilerName: (f: Filing) => string;
  getFilingType: (f: Filing) => string;
  getFilingTypeTag: (filingType: string) => FilingTypeInfo;
  formatDateForDisplay: (date: string) => string;

  // Executive summary focus (optional steering text)
  executiveSummaryFocus: string;
  onExecutiveSummaryFocusChange: (focus: string) => void;

  // Submit
  forceRefresh: boolean;
  onForceRefreshChange: (refresh: boolean) => void;
  onGenerate: () => void;
  isGenerating: boolean;
  error: string | null;
  jobId: string | null;
  periodCount: number;
  periodName: string;
  periodColors: PeriodColors;
  validationError: string | null;
}

// ---------- one collapsible row ----------

const DisclosureRow: React.FC<{
  label: string;
  summary: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ label, summary, open, onToggle, children }) => (
  <div>
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between py-4 text-left transition-colors hover:bg-neutral-50/60"
    >
      <span className="flex items-center gap-2 text-sm font-semibold text-cg-ink">
        {open ? (
          <ChevronDown className="h-4 w-4 text-cg-green" />
        ) : (
          <ChevronRight className="h-4 w-4 text-neutral-400" />
        )}
        {label}
      </span>
      <span className="ml-3 max-w-[60%] truncate text-right text-xs text-neutral-500">{summary}</span>
    </button>
    {open && <div className="pb-4 pl-6">{children}</div>}
  </div>
);

// ---------- main form ----------

const TOPIC_PLACEHOLDERS = ['network neutrality', 'consumer privacy', 'add another topic…'];

export const GenerateSummaryForm: React.FC<GenerateSummaryFormProps> = ({
  topics,
  topicsInputMode,
  onTopicsChange,
  onInputModeChange,
  topicsFirstFieldRef,
  selectionMode,
  selectedFilingIds,
  filings,
  commentsCloseOn,
  onSelectionModeChange,
  onFilingToggle,
  onSelectAll,
  onDeselectAll,
  outOfWindowFilings,
  selectedOutOfWindowIds,
  onOutOfWindowToggle,
  onOutOfWindowSelectAll,
  onOutOfWindowDeselectAll,
  misclassifiedFilings,
  selectedMisclassifiedIds,
  onMisclassifiedToggle,
  onMisclassifiedSelectAll,
  onMisclassifiedDeselectAll,
  detailLevel,
  onDetailLevelChange,
  docketNumber,
  selectedStakeholders,
  onStakeholdersChange,
  showNprmPicker,
  nprmFiles,
  selectedNprmFile,
  onNprmFileChange,
  isLoadingNprmFiles,
  isFetchingNprmFiles,
  fetchingNprmError,
  onFetchNprmFiles,
  nprmCount,
  showPnPicker,
  publicNoticeFiles,
  selectedPublicNoticeFile,
  onPublicNoticeFileChange,
  isLoadingPnFiles,
  isFetchingPnFiles,
  fetchingPnError,
  onFetchPnFiles,
  publicNoticeCount,
  getFilerName,
  getFilingType,
  getFilingTypeTag,
  formatDateForDisplay,
  executiveSummaryFocus,
  onExecutiveSummaryFocusChange,
  forceRefresh,
  onForceRefreshChange,
  onGenerate,
  isGenerating,
  error,
  jobId,
  periodCount,
  periodName,
  periodColors,
  validationError,
}) => {
  // Disclosure open state — everything collapsed by default; users only
  // open what they want to override.
  const [scopeOpen, setScopeOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [stakeholdersOpen, setStakeholdersOpen] = useState(false);
  const [docPickerOpen, setDocPickerOpen] = useState(false);
  const [filingSearch, setFilingSearch] = useState('');

  // Multiple NPRM/PN revisions no longer force a choice: SummariesSection auto-
  // defaults to the most recent so the quick-summary path generates in one
  // click. The picker stays available (users can switch), but never blocks.
  const docPickerMustChoose = false;

  // ----- Topics handlers --------------------------------------------------

  const handleTopicChange = (i: number, value: string) => {
    const next = [...topics];
    next[i] = value;
    onTopicsChange(next);
  };
  const handleRemoveTopic = (i: number) => onTopicsChange(topics.filter((_, idx) => idx !== i));
  const handleAddTopic = () => onTopicsChange([...topics, '']);

  // ----- Scope summaries (collapsed-row text) -----------------------------

  // Late filings within the in-window set (same math as the old wizard fix).
  // Timeline boundaries encode calendar days as midnight-UTC, so we read
  // their components in UTC. Filing timestamps need to be bucketed by
  // Eastern (ECFS local) date — otherwise a 9pm-Eastern deadline-day
  // filing arrives as 01:00 UTC the next day and looks "late".
  const toUtcDateOnly = (dateStr: string): number => {
    if (!dateStr) return NaN;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-').map(Number);
      return Date.UTC(y, m - 1, d);
    }
    const dd = new Date(dateStr);
    return isNaN(dd.getTime()) ? NaN : Date.UTC(dd.getUTCFullYear(), dd.getUTCMonth(), dd.getUTCDate());
  };
  const easternDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
    []
  );
  const toEasternDateOnly = (dateStr: string): number => {
    if (!dateStr) return NaN;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-').map(Number);
      return Date.UTC(y, m - 1, d);
    }
    let dd = new Date(dateStr);
    if (isNaN(dd.getTime())) dd = new Date(dateStr + 'Z');
    if (isNaN(dd.getTime())) return NaN;
    const parts = easternDateFormatter.formatToParts(dd);
    const y = Number(parts.find((p) => p.type === 'year')?.value);
    const m = Number(parts.find((p) => p.type === 'month')?.value);
    const d = Number(parts.find((p) => p.type === 'day')?.value);
    if (!y || !m || !d) return NaN;
    return Date.UTC(y, m - 1, d);
  };
  const closeDateMs = commentsCloseOn ? toUtcDateOnly(commentsCloseOn) : NaN;
  const lateCount = useMemo(() => {
    if (!Number.isFinite(closeDateMs)) return 0;
    return filings.filter((f) => {
      const ms = toEasternDateOnly(f.date_received || f.date_submission || '');
      return Number.isFinite(ms) && ms > closeDateMs;
    }).length;
  }, [filings, closeDateMs, easternDateFormatter]);

  const scopeSummary =
    selectionMode === 'all'
      ? `All ${periodCount} filings${lateCount > 0 && lateCount < periodCount ? ` (${lateCount} late)` : ''}${
          selectedOutOfWindowIds.size > 0
            ? ` + ${selectedOutOfWindowIds.size} out-of-window`
            : ''
        }`
      : `${selectedFilingIds.size} of ${periodCount} selected${
          selectedOutOfWindowIds.size > 0
            ? ` + ${selectedOutOfWindowIds.size} out-of-window`
            : ''
        }`;

  const detailSummary =
    detailLevel === 'concise' ? 'Concise' : detailLevel === 'detailed' ? 'Detailed' : 'Standard';

  const stakeholdersSummary =
    selectedStakeholders.length === 0
      ? 'None — covers all commenters equally'
      : `${selectedStakeholders.length} stakeholder${selectedStakeholders.length === 1 ? '' : 's'} prioritized`;

  const docPickerSummary = showPnPicker
    ? publicNoticeFiles.length === 0
      ? 'No Public Notice document selected'
      : publicNoticeFiles.length === 1
        ? 'Auto-selected (only one PN)'
        : selectedPublicNoticeFile || 'Choose a Public Notice document'
    : showNprmPicker
      ? nprmFiles.length === 0
        ? 'No NPRM document selected'
        : nprmFiles.length === 1
          ? 'Auto-selected (only one NPRM)'
          : selectedNprmFile || 'Choose an NPRM document'
      : null;

  // ----- Filings filtered to search box -----------------------------------

  const matchesSearch = (f: Filing): boolean => {
    if (!filingSearch.trim()) return true;
    const q = filingSearch.toLowerCase();
    return (
      getFilerName(f).toLowerCase().includes(q) ||
      (f.id_submission || '').toLowerCase().includes(q)
    );
  };

  // ----- Validation -------------------------------------------------------

  const hasTopics = topics.filter((t) => t.trim()).length > 0;
  const totalSelected =
    selectionMode === 'all'
      ? periodCount + selectedOutOfWindowIds.size
      : selectedFilingIds.size + selectedOutOfWindowIds.size;
  // At least one topic is required. The generation pipeline and the backend
  // custom_topics validator both reject an empty topic set, so a "blank =
  // balanced overview" path is not implemented end-to-end — enabling the button
  // without a topic only produces a runtime "enter at least one topic" error.
  const canGenerate =
    !isGenerating &&
    hasTopics &&
    totalSelected > 0 &&
    !docPickerMustChoose &&
    !validationError;

  // ----- Render -----------------------------------------------------------

  return (
    <div className="divide-y divide-cg-border/60">
      {/* Topics — always visible, primary */}
      <div className="py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="block text-base font-semibold text-cg-ink">
            Topics{' '}
            <span className="text-gray-400 font-normal text-sm">
              {hasTopics ? '(optional — add more to steer)' : '(enter at least one topic to focus the summary)'}
            </span>
          </div>
          <div className="inline-flex items-center bg-gray-100 rounded-md p-0.5">
            <button
              type="button"
              onClick={() => onInputModeChange('form')}
              className={`px-2 py-0.5 text-xs font-medium rounded ${
                topicsInputMode === 'form' ? 'bg-white text-cg-green shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Form
            </button>
            <button
              type="button"
              onClick={() => onInputModeChange('paragraph')}
              className={`px-2 py-0.5 text-xs font-medium rounded ${
                topicsInputMode === 'paragraph' ? 'bg-white text-cg-green shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Paragraph
            </button>
          </div>
        </div>

        {topicsInputMode === 'form' ? (
          <div className="space-y-2">
            {topics.map((topic, index) => (
              <div key={index} className="flex items-center space-x-2">
                <span className="flex-shrink-0 w-6 text-center text-sm text-gray-500">{index + 1}.</span>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => handleTopicChange(index, e.target.value)}
                  placeholder={`e.g., ${TOPIC_PLACEHOLDERS[index] || 'add another topic…'}`}
                  ref={index === 0 ? (topicsFirstFieldRef as React.Ref<HTMLInputElement>) : undefined}
                  className="flex-1 px-3 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-cg-green/30 focus:border-cg-green text-sm"
                />
                {topics.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveTopic(index)}
                    className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddTopic}
              className="flex items-center gap-1.5 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add topic</span>
            </button>
          </div>
        ) : (
          <textarea
            value={topics.join('\n')}
            onChange={(e) => onTopicsChange(e.target.value.split('\n').map((t) => t.trim()))}
            placeholder="Enter each topic on a new line…"
            ref={topicsFirstFieldRef as React.Ref<HTMLTextAreaElement>}
            rows={5}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-cg-green/30 focus:border-cg-green text-sm resize-y"
          />
        )}
      </div>

      {/* Executive summary focus — primary, always visible, optional */}
      <div className="py-4">
        <label className="block text-sm font-semibold text-gray-900 mb-1" htmlFor="exec-focus-input">
          Executive summary focus <span className="text-xs font-normal text-gray-500">(optional)</span>
        </label>
        <p className="text-xs text-gray-500 mb-2">
          What should the exec emphasize? Naming specific arguments, stakeholders, or
          decisions you care about produces a sharper summary than the default
          "describe the landscape" tendency. Leave blank for a balanced overview.
        </p>
        <textarea
          id="exec-focus-input"
          value={executiveSummaryFocus}
          onChange={(e) => onExecutiveSummaryFocusChange(e.target.value)}
          placeholder="e.g., Whether processing rounds should replace expedited licensing. Lead with NRAO's and FCC Bureau's positions."
          rows={3}
          className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-cg-green/30 focus:border-cg-green"
        />
      </div>

      {/* Scope */}
      <DisclosureRow
        label="Scope"
        summary={scopeSummary}
        open={scopeOpen}
        onToggle={() => setScopeOpen((v) => !v)}
      >
        {/* in-window selection mode */}
        <div className="space-y-2 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={selectionMode === 'all'}
              onChange={() => onSelectionModeChange('all')}
              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              All filings in the {periodName.toLowerCase()} window ({periodCount}
              {lateCount > 0 && lateCount < periodCount && commentsCloseOn ? (
                <>
                  , including <span className="text-amber-700">{lateCount} filed late</span>
                </>
              ) : null}
              )
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={selectionMode === 'specific'}
              onChange={() => onSelectionModeChange('specific')}
              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Select specific filings</span>
          </label>
        </div>

        {selectionMode === 'specific' && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-700">
                {selectedFilingIds.size} of {periodCount} selected
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onSelectAll}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Select all
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={onDeselectAll}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Deselect all
                </button>
              </div>
            </div>
            <div className="relative mb-2">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={filingSearch}
                onChange={(e) => setFilingSearch(e.target.value)}
                placeholder="Search by filer or submission ID…"
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="max-h-72 overflow-y-auto space-y-1">
              {filings.filter(matchesSearch).map((f) => {
                const sel = selectedFilingIds.has(f.id_submission);
                const ms = toEasternDateOnly(f.date_received || f.date_submission || '');
                const isLate =
                  Number.isFinite(closeDateMs) && Number.isFinite(ms) && ms > closeDateMs;
                return (
                  <label
                    key={f.id_submission}
                    className="flex items-start gap-2 p-1.5 hover:bg-white rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={sel}
                      onChange={() => onFilingToggle(f.id_submission)}
                      className="mt-0.5"
                    />
                    <span className="flex-1 text-xs">
                      <span className="font-medium text-gray-900">{getFilerName(f)}</span>
                      <span className="text-gray-500">
                        {' · '}
                        {formatDateForDisplay(f.date_received || f.date_submission || '')}
                      </span>
                      {isLate && (
                        <span className="ml-1 text-amber-700 text-[10px]">(late)</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Out-of-window opt-in */}
        {outOfWindowFilings.length > 0 && (
          <div className="mt-4 p-3 bg-amber-50/50 border border-amber-200 rounded-md">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-medium text-amber-900">
                Filings outside the {periodName.toLowerCase()} window
              </div>
              <div className="flex items-center gap-2 text-[11px]">
                <span className="text-amber-900/80">
                  {selectedOutOfWindowIds.size} of {outOfWindowFilings.length} selected
                </span>
                <button
                  type="button"
                  onClick={onOutOfWindowSelectAll}
                  className="text-amber-800 hover:underline"
                >
                  Select all
                </button>
                <span className="text-amber-300">|</span>
                <button
                  type="button"
                  onClick={onOutOfWindowDeselectAll}
                  className="text-amber-800 hover:underline"
                >
                  Deselect all
                </button>
              </div>
            </div>
            <div className="text-[11px] text-amber-800 mb-2">
              Filed before the window opened or after the reply close. Opt in any that
              contain content worth summarizing — they aren't included by default.
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {outOfWindowFilings.map((f) => {
                const sel = selectedOutOfWindowIds.has(f.id_submission);
                return (
                  <label
                    key={f.id_submission}
                    className="flex items-start gap-2 p-1.5 hover:bg-white rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={sel}
                      onChange={() => onOutOfWindowToggle(f.id_submission)}
                      className="mt-0.5"
                    />
                    <span className="flex-1 text-xs">
                      <span className="font-medium text-gray-900">{getFilerName(f)}</span>
                      <span className="text-gray-500">
                        {' · '}
                        {formatDateForDisplay(f.date_received || f.date_submission || '')}
                        {' · '}
                        {getFilingType(f) || '—'}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Mis-classified opt-in: filings tagged as NPRM / Notice / etc.
           that the user may have identified as actually being comments. */}
        {misclassifiedFilings.length > 0 && (
          <div className="mt-4 p-3 bg-rose-50/50 border border-rose-200 rounded-md">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-medium text-rose-900">
                Other docket filings (different submission types)
              </div>
              <div className="flex items-center gap-2 text-[11px]">
                <span className="text-rose-900/80">
                  {selectedMisclassifiedIds.size} of {misclassifiedFilings.length} selected
                </span>
                <button
                  type="button"
                  onClick={onMisclassifiedSelectAll}
                  className="text-rose-800 hover:underline"
                >
                  Select all
                </button>
                <span className="text-rose-300">|</span>
                <button
                  type="button"
                  onClick={onMisclassifiedDeselectAll}
                  className="text-rose-800 hover:underline"
                >
                  Deselect all
                </button>
              </div>
            </div>
            <div className="text-[11px] text-rose-800 mb-2">
              These weren't tagged as comments on ECFS. If you recognize one as
              actually being a comment that was mis-classified, opt it in here
              and it will be included in the summary.
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {misclassifiedFilings.map((f) => {
                const sel = selectedMisclassifiedIds.has(f.id_submission);
                const ft = getFilingType(f) || 'UNKNOWN';
                return (
                  <label
                    key={f.id_submission}
                    className="flex items-start gap-2 p-1.5 hover:bg-white rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={sel}
                      onChange={() => onMisclassifiedToggle(f.id_submission)}
                      className="mt-0.5"
                    />
                    <span className="flex-1 text-xs">
                      <span className="font-medium text-gray-900">{getFilerName(f)}</span>
                      <span className="text-gray-500">
                        {' · '}
                        {formatDateForDisplay(f.date_received || f.date_submission || '')}
                      </span>
                      <span className="ml-1 inline-block px-1 py-[1px] text-[10px] font-semibold rounded bg-rose-100 text-rose-800 uppercase tracking-wide">
                        {ft}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </DisclosureRow>

      {/* Detail level */}
      <DisclosureRow
        label="Detail level"
        summary={detailSummary}
        open={detailOpen}
        onToggle={() => setDetailOpen((v) => !v)}
      >
        <div className="space-y-2">
          {(['concise', 'standard', 'detailed'] as const).map((lvl) => (
            <label key={lvl} className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                checked={detailLevel === lvl}
                onChange={() => onDetailLevelChange(lvl)}
                className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <div className="text-sm">
                <span className="font-medium text-gray-900 capitalize">{lvl}</span>
                <span className="block text-xs text-gray-500">
                  {lvl === 'concise' && 'Shorter per-topic summaries; fastest to generate.'}
                  {lvl === 'standard' && 'Default. Balanced between depth and length.'}
                  {lvl === 'detailed' && 'Deepest extraction; more documents processed per topic.'}
                </span>
              </div>
            </label>
          ))}
        </div>
      </DisclosureRow>

      {/* Stakeholders */}
      <DisclosureRow
        label="Focus on stakeholders"
        summary={stakeholdersSummary}
        open={stakeholdersOpen}
        onToggle={() => setStakeholdersOpen((v) => !v)}
      >
        <StakeholderSelector
          docketNumber={docketNumber}
          selectedStakeholders={selectedStakeholders}
          onChange={onStakeholdersChange}
        />
      </DisclosureRow>

      {/* Document picker (NPRM or PN) */}
      {(showNprmPicker || showPnPicker) && (
        <DisclosureRow
          label={showPnPicker ? 'Public Notice document' : 'NPRM document'}
          summary={docPickerSummary || '—'}
          open={docPickerOpen || docPickerMustChoose}
          onToggle={() => setDocPickerOpen((v) => !v)}
        >
          {showPnPicker ? (
            <DocPicker
              kind="pn"
              files={publicNoticeFiles}
              selectedFile={selectedPublicNoticeFile}
              onChange={onPublicNoticeFileChange}
              isLoading={isLoadingPnFiles}
              isFetching={isFetchingPnFiles}
              fetchError={fetchingPnError}
              onFetch={onFetchPnFiles}
              count={publicNoticeCount}
            />
          ) : (
            <DocPicker
              kind="nprm"
              files={nprmFiles}
              selectedFile={selectedNprmFile}
              onChange={onNprmFileChange}
              isLoading={isLoadingNprmFiles}
              isFetching={isFetchingNprmFiles}
              fetchError={fetchingNprmError}
              onFetch={onFetchNprmFiles}
              count={nprmCount}
            />
          )}
        </DisclosureRow>
      )}

      {/* Validation banner */}
      {validationError && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">{validationError}</p>
        </div>
      )}

      {/* Generate button row — hero CTA, this is the whole point of the page. */}
      <div className="pt-5">
        <button
          type="button"
          onClick={onGenerate}
          disabled={!canGenerate}
          className={`flex w-full items-center justify-center gap-2.5 rounded-xl ${periodColors.button} px-6 py-4 text-base font-semibold text-white shadow-md transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-md sm:text-lg`}
        >
          {isGenerating ? <Loader className="h-5 w-5 animate-spin" /> : <Wand2 className="h-5 w-5" />}
          <span>{isGenerating ? 'Generating…' : 'Generate summary'}</span>
        </button>
        <label className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500 cursor-pointer">
          <input
            type="checkbox"
            checked={forceRefresh}
            onChange={(e) => onForceRefreshChange(e.target.checked)}
            className="w-3.5 h-3.5"
          />
          Force regenerate (ignore cached summary)
        </label>
      </div>

      {/* Inline error / job-id banner (from the old ReviewStep). A usage-limit
          error gets an actionable "View plans" CTA — otherwise the message tells
          the user to upgrade but gives them nowhere to do it. */}
      {error && (() => {
        // Only billing/quota errors carry "upgrade your plan" (client pre-check
        // + backend 403 detail). Transient infra errors also say "limit" (e.g.
        // "Rate limit exceeded for gpt-4o…") but must NOT show an upgrade CTA —
        // the user should reduce scope, not pay. Match the billing phrase, not
        // bare "limit".
        const isUpgrade = /upgrade your plan/i.test(error);
        return (
          <div className={`p-3 rounded-md flex items-start gap-2 border ${isUpgrade ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
            <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isUpgrade ? 'text-amber-700' : 'text-red-700'}`} />
            <div className={`text-sm ${isUpgrade ? 'text-amber-900' : 'text-red-800'}`}>
              <p>{error}</p>
              {isUpgrade && (
                <Link
                  to="/pricing"
                  className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 transition-colors"
                >
                  View plans <ArrowRight className="w-3 h-3" />
                </Link>
              )}
              {!isUpgrade && jobId && <p className="mt-1 text-xs">Job ID: {jobId}</p>}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// ---------- doc picker (NPRM or PN) ----------

const DocPicker: React.FC<{
  kind: 'nprm' | 'pn';
  files: NprmFile[];
  selectedFile: string | null;
  onChange: (file: string | null) => void;
  isLoading: boolean;
  isFetching: boolean;
  fetchError: string | null;
  onFetch: () => void;
  count: number | null;
}> = ({ kind, files, selectedFile, onChange, isLoading, isFetching, fetchError, onFetch, count }) => {
  const label = kind === 'pn' ? 'Public Notice' : 'NPRM';
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
        <Loader className="w-4 h-4 animate-spin" />
        Loading {label} files…
      </div>
    );
  }
  if (files.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-600">
          {count && count > 0
            ? `${count} ${label} document${count === 1 ? '' : 's'} found on the docket but not yet pulled into storage.`
            : `No ${label} documents found on the docket yet.`}
        </p>
        {count && count > 0 && (
          <button
            type="button"
            onClick={onFetch}
            disabled={isFetching}
            className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded"
          >
            {isFetching ? 'Fetching…' : `Fetch ${label} documents`}
          </button>
        )}
        {fetchError && <p className="text-xs text-red-600">{fetchError}</p>}
      </div>
    );
  }
  return (
    <div className="space-y-1.5 max-h-48 overflow-y-auto">
      {files.map((f) => (
        <label key={f.name} className="flex items-start gap-2 cursor-pointer p-1 hover:bg-gray-50 rounded">
          <input
            type="radio"
            name={`doc-picker-${kind}`}
            checked={selectedFile === f.name}
            onChange={() => onChange(f.name)}
            className="mt-0.5"
          />
          <div className="text-xs">
            <span className="font-medium text-gray-900">{f.name}</span>
            <span className="block text-gray-500">
              {f.created_at ? new Date(f.created_at).toLocaleDateString() : ''}
            </span>
          </div>
        </label>
      ))}
    </div>
  );
};
